import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, Sparkles, ExternalLink,
  User, Wallet, Home, TrendingUp, Briefcase, PiggyBank, Receipt, FileCheck, ShieldCheck,
  Download, FileJson, FileText, ClipboardList, Loader2, Info, Users,
  Smartphone, Globe, Key, CreditCard, PartyPopper,
} from "lucide-react";
import { inr } from "@/lib/format";
import PageHeader from "@/components/business/PageHeader";
import StatusBanner from "@/components/business/StatusBanner";

const STEPS = [
  { id: "personal", label: "Personal", icon: User },
  { id: "salary", label: "Salary", icon: Wallet },
  { id: "house", label: "House property", icon: Home },
  { id: "capital", label: "Capital gains", icon: TrendingUp },
  { id: "business", label: "Business / Other", icon: Briefcase },
  { id: "deductions", label: "Deductions", icon: PiggyBank },
  { id: "tds", label: "TDS & Bank", icon: Receipt },
  { id: "review", label: "Review", icon: FileCheck },
  { id: "audit", label: "AI Audit", icon: ShieldCheck },
  { id: "prepare", label: "Prepare & Download", icon: Download },
];

const STATUS_LABEL = {
  draft: "Draft",
  prepared: "Prepared — awaiting Government submission",
  awaiting_user_submission: "Ready to upload — awaiting Government submission",
  ca_requested: "CA assistance requested",
  ca_in_review: "Under CA review",
  ca_docs_pending: "Awaiting your documents",
  ca_filing: "Being filed by CA at incometax.gov.in",
  filed_with_government: "Filed with the Income Tax Department",
};

export default function Filing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const filingIdFromUrl = params.get("id");

  const [filings, setFilings] = useState([]);
  const [filing, setFiling] = useState(null);
  const [data, setData] = useState({});
  const [step, setStep] = useState("personal");
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [prepareResult, setPrepareResult] = useState(null);
  const [validation, setValidation] = useState(null);
  const [caDialogOpen, setCaDialogOpen] = useState(false);
  const [caNotes, setCaNotes] = useState("");
  const [itrVAck, setItrVAck] = useState("");

  useEffect(() => {
    (async () => {
      const list = (await api.get("/filings")).data;
      setFilings(list);
      const target = filingIdFromUrl ? list.find((x) => x.id === filingIdFromUrl) : list[0];
      if (target) {
        setFiling(target);
        setData(target.data || {});
        setStep(target.current_step || "personal");
      }
    })();
  }, [filingIdFromUrl]);

  const createNew = async (itr_type = "ITR-1") => {
    setCreating(true);
    try {
      const { data: f } = await api.post("/filings", { itr_type, assessment_year: "2026-27", regime: "new" });
      setFiling(f); setData({}); setStep("personal");
      setFilings([f, ...filings]);
      navigate(`/filing?id=${f.id}`, { replace: true });
    } finally {
      setCreating(false);
    }
  };

  const save = async (next) => {
    if (!filing) return;
    setSaving(true);
    try {
      const payload = { step: next || step, data };
      const { data: updated } = await api.patch(`/filings/${filing.id}`, payload);
      setFiling(updated);
      if (next) setStep(next);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const runAudit = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.post(`/filings/${filing.id}/audit`);
      setAudit(r);
    } finally {
      setSaving(false);
    }
  };

  const prepare = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.post(`/filings/${filing.id}/prepare`);
      setPrepareResult(r);
      setValidation(r.validation);
      const { data: f } = await api.get(`/filings/${filing.id}`);
      setFiling(f);
      toast.success("Your return is prepared — review the downloads below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't prepare");
    } finally {
      setSaving(false);
    }
  };

  const confirmSelfSubmission = async () => {
    try {
      const { data: r } = await api.post(`/filings/${filing.id}/confirm-self-submission`);
      const { data: f } = await api.get(`/filings/${filing.id}`);
      setFiling(f);
      toast.success("Your status is now: " + r.status_label);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    }
  };

  const requestCa = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.post(`/filings/${filing.id}/request-ca`, { notes: caNotes });
      setCaDialogOpen(false);
      const { data: f } = await api.get(`/filings/${filing.id}`);
      setFiling(f);
      toast.success(`Request received. Assigned to ${r.ca?.name || "our CA team"}.`);
      navigate(`/ca-flow/${filing.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't request CA");
    } finally {
      setSaving(false);
    }
  };

  const markSelfFiled = async () => {
    const ack = itrVAck.trim();
    if (ack.length < 12) {
      toast.error("Please enter the full ITR-V acknowledgement number (minimum 12 characters).");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/filings/${filing.id}/mark-self-filed`, { itr_v_acknowledgement: ack });
      const { data: f } = await api.get(`/filings/${filing.id}`);
      setFiling(f);
      toast.success("Return recorded as filed. Acknowledgement saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save acknowledgement");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const computed = filing?.computed_tax;
  const regime = filing?.regime || "new";

  const downloadUrl = (path) => {
    const token = localStorage.getItem("crs_token");
    return `${API_BASE}${path}?token=${encodeURIComponent(token)}`;
  };

  const triggerDownload = async (relPath, filename) => {
    try {
      const resp = await api.get(relPath, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download " + filename);
    }
  };

  if (!filing) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto py-6">
          <PageHeader>
            <PageHeader.Title>Start a New Return</PageHeader.Title>
          </PageHeader>
          <p className="mt-2 text-sm text-muted-foreground">Pick the ITR form that matches your income sources. Not sure? Start with ITR-1 — we'll switch automatically if needed.</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["ITR-1", "ITR-2", "ITR-3", "ITR-4"].map((t) => (
              <Card key={t} className="p-6 border-border hover:border-primary transition-colors cursor-pointer" onClick={() => createNew(t)} data-testid={`new-${t.toLowerCase()}`}>
                <div className="font-display text-lg font-semibold">{t}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {{ "ITR-1": "Salaried, one house, total income up to ₹50L",
                     "ITR-2": "Capital gains, multiple houses, foreign income",
                     "ITR-3": "Business or profession income",
                     "ITR-4": "Presumptive scheme (44AD/44ADA/44AE)" }[t]}
                </div>
                <Button className="mt-5 w-full" disabled={creating}>Start {t} {creating ? <Loader2 className="ml-2 h-4 w-4 animate-spin" /> : <ChevronRight className="ml-2 h-4 w-4" />}</Button>
              </Card>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  const idx = STEPS.findIndex((s) => s.id === step);
  const prev = STEPS[idx - 1];
  const next = STEPS[idx + 1];
  const inCaWorkflow = ["ca_requested", "ca_in_review", "ca_docs_pending", "ca_filing", "filed_with_government"].includes(filing.status);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <div>
            <PageHeader.Title eyebrow={`${filing.itr_type} · AY ${filing.assessment_year}`}>Filing wizard</PageHeader.Title>
            <div className="mt-2 text-xs">
              <span className="rounded-full bg-muted px-2.5 py-0.5 font-medium" data-testid="filing-status-label">
                {STATUS_LABEL[filing.status] || filing.status}
              </span>
            </div>
          </div>
          <PageHeader.Actions>
            {!inCaWorkflow && (
              <Select value={regime} onValueChange={(v) => api.patch(`/filings/${filing.id}`, { regime: v }).then(({ data }) => setFiling(data))}>
                <SelectTrigger className="h-10 w-44" data-testid="regime-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New regime</SelectItem>
                  <SelectItem value="old">Old regime</SelectItem>
                </SelectContent>
              </Select>
            )}
            {inCaWorkflow ? (
              <Button asChild variant="outline"><Link to={`/ca-flow/${filing.id}`}>Open CA workspace →</Link></Button>
            ) : (
              <Button variant="outline" onClick={() => createNew("ITR-1")}>New filing</Button>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {/* Trust banner — always visible until truly filed with Govt */}
        {filing.status !== "filed_with_government" && (
          <StatusBanner variant="warning" title="This return has not been filed yet." className="mt-4" data-testid="not-filed-banner">
            CA Ritika Singh is not an e-Return Intermediary (ERI). Until our ERI integration is live, filing is completed only after your return is uploaded at <a className="underline" href="https://incometax.gov.in" target="_blank" rel="noreferrer">incometax.gov.in</a> and an official ITR-V acknowledgement is received — either by you (Option A) or by a Chartered Accountant from our team (Option B).
          </StatusBanner>
        )}

        {filing.status === "filed_with_government" && (
          <StatusBanner variant="success" title="Filed with the Income Tax Department" className="mt-4">
            Official ITR-V acknowledgement: <span className="font-mono font-semibold">{filing.itr_v_acknowledgement}</span>
          </StatusBanner>
        )}

        {/* STEPPER */}
        {!inCaWorkflow && (
          <div className="mt-6 overflow-x-auto">
            <ol className="flex gap-1 min-w-max" data-testid="stepper">
              {STEPS.map((s, i) => {
                const Ic = s.icon;
                const active = s.id === step;
                const done = i < idx;
                return (
                  <li key={s.id} className="flex items-center">
                    <button
                      onClick={() => setStep(s.id)}
                      data-testid={`step-${s.id}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                        active ? "bg-primary text-primary-foreground step-glow" :
                        done ? "text-accent" : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <Ic className="h-4 w-4" />
                      <span className="hidden sm:inline">{s.label}</span>
                    </button>
                    {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5" />}
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6 border-border lg:col-span-2">
            {step === "personal" && <PersonalStep data={data} set={setField} />}
            {step === "salary" && <SalaryStep data={data} set={setField} />}
            {step === "house" && <HouseStep data={data} set={setField} />}
            {step === "capital" && <CapitalStep data={data} set={setField} />}
            {step === "business" && <BusinessStep data={data} set={setField} />}
            {step === "deductions" && <DeductionsStep data={data} set={setField} regime={regime} />}
            {step === "tds" && <TdsStep data={data} set={setField} />}
            {step === "review" && <ReviewStep data={data} filing={filing} />}
            {step === "audit" && <AuditStep audit={audit} runAudit={runAudit} saving={saving} />}
            {step === "prepare" && (
              <PrepareStep
                filing={filing}
                prepare={prepare}
                saving={saving}
                triggerDownload={triggerDownload}
                validation={validation}
                confirmSelfSubmission={confirmSelfSubmission}
                caDialogOpen={caDialogOpen}
                setCaDialogOpen={setCaDialogOpen}
                caNotes={caNotes}
                setCaNotes={setCaNotes}
                requestCa={requestCa}
                itrVAck={itrVAck}
                setItrVAck={setItrVAck}
                markSelfFiled={markSelfFiled}
              />
            )}

            {!inCaWorkflow && (
              <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
                <Button variant="outline" disabled={!prev} onClick={() => prev && save(prev.id)}>
                  <ChevronLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" onClick={() => save()} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  {next ? (
                    <Button onClick={() => save(next.id)} disabled={saving} data-testid="next-btn">
                      Save & continue <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-6 border-border h-fit lg:sticky lg:top-20">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Live calculation</div>
            <div className="mt-2 font-display text-lg font-semibold">Tax under {regime} regime</div>

            {computed ? (
              <div className="mt-5 space-y-3">
                <Row label="Gross income" val={inr(computed[regime]?.gross_total_income)} />
                <Row label="Std. deduction" val={`− ${inr(computed[regime]?.standard_deduction)}`} />
                {regime === "old" && <Row label="HRA exempt" val={`− ${inr(computed[regime]?.hra_exemption)}`} />}
                <Row label="Total deductions" val={`− ${inr(computed[regime]?.total_deductions)}`} />
                <div className="h-px bg-border my-2" />
                <Row label="Taxable income" val={inr(computed[regime]?.taxable_income)} bold />
                <Row label="Tax" val={inr(computed[regime]?.tax_before_rebate)} />
                <Row label="87A rebate" val={`− ${inr(computed[regime]?.rebate_87a)}`} />
                <Row label="Cess (4%)" val={inr(computed[regime]?.cess)} />
                <div className="h-px bg-border my-2" />
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Total tax</div>
                  <div className="num-rupee font-display text-2xl font-bold text-accent">{inr(computed[regime]?.total_tax)}</div>
                </div>

                {computed.diff !== 0 && (
                  <div className={`mt-5 p-3 rounded-md text-xs ${computed.recommended === regime ? "bg-accent/10 text-accent" : "bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
                    {computed.recommended === regime
                      ? <>✓ You're on the recommended regime. Saving ~{inr(computed.savings)} vs {regime === "new" ? "old" : "new"}.</>
                      : <>⚠ Switch to {computed.recommended} regime — saves ~{inr(computed.savings)}.</>}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 text-sm text-muted-foreground">Add salary or income to see live tax computation.</div>
            )}
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

const Row = ({ label, val, bold }) => (
  <div className="flex items-center justify-between">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`num-rupee text-sm ${bold ? "font-bold" : "font-medium"}`}>{val}</div>
  </div>
);

// ============== STEPS ============== //

function PersonalStep({ data, set }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Personal details</h2>
      <p className="mt-1 text-sm text-muted-foreground">Verify your basic identity for the return.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="PAN"><Input data-testid="f-pan" value={data.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} maxLength={10} className="h-11 font-mono uppercase" /></Field>
        <Field label="Aadhaar (12 digits)"><Input data-testid="f-aadhaar" value={data.aadhaar || ""} onChange={(e) => set("aadhaar", e.target.value)} className="h-11" /></Field>
        <Field label="Date of birth"><Input data-testid="f-dob" type="date" value={data.dob || ""} onChange={(e) => set("dob", e.target.value)} className="h-11" /></Field>
        <Field label="Residential status">
          <Select value={data.residential_status || "resident"} onValueChange={(v) => set("residential_status", v)}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="resident">Resident & Ordinarily Resident</SelectItem>
              <SelectItem value="rnor">Resident but Not Ordinarily Resident</SelectItem>
              <SelectItem value="nri">Non-resident</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Email"><Input data-testid="f-email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} className="h-11" /></Field>
        <Field label="Mobile"><Input data-testid="f-mobile" value={data.mobile || ""} onChange={(e) => set("mobile", e.target.value)} className="h-11" /></Field>
        <Field label="Father's name (for verification)"><Input value={data.father_name || ""} onChange={(e) => set("father_name", e.target.value)} className="h-11" /></Field>
        <Field label="City"><Input value={data.city || ""} onChange={(e) => set("city", e.target.value)} className="h-11" /></Field>
        <Field label="PIN code"><Input value={data.pincode || ""} onChange={(e) => set("pincode", e.target.value)} maxLength={6} className="h-11" /></Field>
      </div>
    </div>
  );
}

function SalaryStep({ data, set }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Salary income</h2>
      <p className="mt-1 text-sm text-muted-foreground">Enter values from your Form 16. Upload Form 16 from the Documents page to auto-fill.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <NField label="Gross salary (₹)" k="salary_gross" data={data} set={set} />
        <NField label="HRA received (₹)" k="hra_received" data={data} set={set} />
        <NField label="Rent paid annually (₹)" k="rent_paid" data={data} set={set} />
        <Field label="City type">
          <Select value={data.metro_city ? "metro" : "non_metro"} onValueChange={(v) => set("metro_city", v === "metro")}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="metro">Metro (Delhi, Mumbai, Kolkata, Chennai)</SelectItem>
              <SelectItem value="non_metro">Non-metro</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <NField label="Professional tax paid (₹)" k="professional_tax" data={data} set={set} />
        <NField label="Other employer perquisites (₹)" k="perquisites" data={data} set={set} />
      </div>
    </div>
  );
}

function HouseStep({ data, set }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">House property income</h2>
      <p className="mt-1 text-sm text-muted-foreground">Self-occupied home loan interest is deductible up to ₹2,00,000.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <NField label="Net rental income (₹/yr)" k="house_property_income" data={data} set={set} />
        <NField label="Home loan interest paid (₹)" k="home_loan_interest" data={data} set={set} />
      </div>
    </div>
  );
}

function CapitalStep({ data, set }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Capital gains</h2>
      <p className="mt-1 text-sm text-muted-foreground">From listed equity, mutual funds, crypto or property. Budget 2024 rates apply from 23 July 2024.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <NField label="Short-term capital gain" k="capital_gains_short" data={data} set={set} />
        <NField label="Long-term capital gain (12.5%)" k="capital_gains_long" data={data} set={set} />
      </div>
    </div>
  );
}

function BusinessStep({ data, set }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Business / freelance / other</h2>
      <p className="mt-1 text-sm text-muted-foreground">Freelancers can opt for the presumptive scheme under Section 44ADA.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <NField label="Business income (₹)" k="business_income" data={data} set={set} />
        <NField label="Freelance / professional income (₹)" k="freelance_income" data={data} set={set} />
        <NField label="Interest income (savings + FD)" k="interest_income" data={data} set={set} />
        <NField label="Dividend income (₹)" k="dividend_income" data={data} set={set} />
        <NField label="Other sources (₹)" k="other_sources" data={data} set={set} />
        <NField label="Agricultural income (exempt)" k="agri_income" data={data} set={set} />
      </div>
    </div>
  );
}

function DeductionsStep({ data, set, regime }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Deductions & exemptions</h2>
      <p className="mt-1 text-sm text-muted-foreground">{regime === "new" ? "Note: New regime allows std. deduction (₹75,000), employer NPS, and Agniveer fund only." : "Old regime — claim everything you've invested in."}</p>
      <div className={`mt-6 grid sm:grid-cols-2 gap-4 ${regime === "new" ? "opacity-50 pointer-events-none" : ""}`}>
        <NField label="80C (ELSS, PPF, EPF, LIC) — max ₹1,50,000" k="sec_80c" data={data} set={set} />
        <NField label="80CCD(1B) NPS — max ₹50,000" k="sec_80ccd_1b" data={data} set={set} />
        <NField label="80D Health insurance — max ₹1,00,000" k="sec_80d" data={data} set={set} />
        <NField label="80E Education loan interest" k="sec_80e" data={data} set={set} />
        <NField label="80G Donations (eligible portion)" k="sec_80g" data={data} set={set} />
        <NField label="80TTA Savings interest — max ₹10,000" k="sec_80tta" data={data} set={set} />
        <NField label="Other (80EEA, 80DD, 80U etc.)" k="other_ded" data={data} set={set} />
      </div>
    </div>
  );
}

function TdsStep({ data, set }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">TDS, advance tax & bank details</h2>
      <p className="mt-1 text-sm text-muted-foreground">Refunds are credited only to a pre-validated bank account at incometax.gov.in.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <NField label="TDS deducted (Form 16 Part A)" k="tds_salary" data={data} set={set} />
        <NField label="TDS - other income" k="tds_other" data={data} set={set} />
        <NField label="Advance tax paid" k="advance_tax" data={data} set={set} />
        <NField label="Self-assessment tax" k="self_assessment_tax" data={data} set={set} />
        <Field label="Bank account number"><Input value={data.bank_account || ""} onChange={(e) => set("bank_account", e.target.value)} className="h-11" /></Field>
        <Field label="IFSC (11 chars)"><Input value={data.bank_ifsc || ""} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} maxLength={11} className="h-11 font-mono uppercase" /></Field>
        <Field label="Bank name"><Input value={data.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} className="h-11" /></Field>
      </div>
    </div>
  );
}

function ReviewStep({ data, filing }) {
  const computed = filing.computed_tax;
  const regime = filing.regime;
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Review</h2>
      <p className="mt-1 text-sm text-muted-foreground">Confirm every section. You can edit any step before preparing the return.</p>

      {computed && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <RegimeCard label="Old regime" data={computed.old} active={regime === "old"} />
          <RegimeCard label="New regime" data={computed.new} active={regime === "new"} />
        </div>
      )}

      <div className="mt-8">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your inputs</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {Object.entries(data || {}).filter(([_, v]) => v !== "" && v !== 0 && v !== null && v !== undefined).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-border/50 py-1.5">
              <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
              <span className="font-medium num-rupee">{typeof v === "number" ? inr(v) : String(v)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RegimeCard({ label, data, active }) {
  if (!data) return null;
  return (
    <Card className={`p-5 ${active ? "border-primary" : "border-border"}`}>
      <div className="flex items-center justify-between">
        <div className="font-display font-semibold">{label}</div>
        {active && <span className="text-[10px] uppercase tracking-[0.18em] text-accent">Current</span>}
      </div>
      <div className="mt-3 num-rupee font-display text-2xl font-bold">{inr(data.total_tax)}</div>
      <div className="mt-3 text-xs text-muted-foreground space-y-1">
        <div>Taxable: {inr(data.taxable_income)}</div>
        <div>87A rebate: {inr(data.rebate_87a)}</div>
        <div>Effective rate: {data.effective_rate}%</div>
      </div>
    </Card>
  );
}

function AuditStep({ audit, runAudit, saving }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">AI Audit · Pre-submission checks</h2>
      <p className="mt-1 text-sm text-muted-foreground">Before preparing the final return we validate PAN, IFSC, deductions, regime selection, capital-gain math and more.</p>
      <div className="mt-6">
        <Button onClick={runAudit} disabled={saving} data-testid="run-audit-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Run AI Audit
        </Button>
      </div>

      {audit && (
        <div className="mt-6 space-y-4">
          <Card className="p-5 border-border">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold">{audit.ok ? "All critical checks passed" : "Issues found"}</div>
              <div className="text-sm num-rupee">{audit.passed_checks}/{audit.total_checks} passed</div>
            </div>
          </Card>
          {audit.issues.map((i, k) => (
            <div key={k} className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
              <div><span className="font-semibold">{i.code}:</span> {i.msg}</div>
            </div>
          ))}
          {audit.warnings.map((w, k) => (
            <div key={k} className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div><span className="font-semibold">{w.code}:</span> {w.msg}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrepareStep({
  filing, prepare, saving, triggerDownload, validation, confirmSelfSubmission,
  caDialogOpen, setCaDialogOpen, caNotes, setCaNotes, requestCa,
  itrVAck, setItrVAck, markSelfFiled,
}) {
  const isPrepared = ["prepared", "awaiting_user_submission", "filed_with_government"].includes(filing.status);
  const isAwaitingSubmission = filing.status === "awaiting_user_submission";
  const isFiled = filing.status === "filed_with_government";

  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Prepare your return & download official artifacts</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        This finalizes your tax computation and produces the JSON file in the Income Tax Department schema, along with PDF reports. Nothing is submitted to the Government — that's the next step, and you choose how.
      </p>

      {!isPrepared && (
        <div className="mt-6">
          <Button onClick={prepare} disabled={saving} data-testid="prepare-btn" className="bg-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Prepare my return
          </Button>
        </div>
      )}

      {isPrepared && (
        <>
          <StatusBanner variant="success" title="Your Income Tax Return has been successfully prepared." className="mt-6">
            It has <span className="font-semibold">not yet been submitted to the Income Tax Department of India.</span> Choose Option A or Option B below to complete filing.
          </StatusBanner>

          {/* Downloads */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Downloads</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <DownloadCard
                icon={FileJson}
                title="ITR JSON (Government schema)"
                desc="Upload this at incometax.gov.in"
                onClick={() => triggerDownload(`/filings/${filing.id}/export/itr.json`, `itr-${filing.id.slice(0,8)}.json`)}
                testid="dl-json"
              />
              <DownloadCard
                icon={FileText}
                title="Tax computation report (PDF)"
                desc="Full breakdown — income, deductions, tax"
                onClick={() => triggerDownload(`/filings/${filing.id}/export/computation.pdf`, `computation-${filing.id.slice(0,8)}.pdf`)}
                testid="dl-computation"
              />
              <DownloadCard
                icon={FileText}
                title="Filing summary (PDF)"
                desc="One-page overview"
                onClick={() => triggerDownload(`/filings/${filing.id}/export/summary.pdf`, `summary-${filing.id.slice(0,8)}.pdf`)}
                testid="dl-summary"
              />
              <DownloadCard
                icon={ClipboardList}
                title="Document checklist (PDF)"
                desc="What to keep ready while uploading"
                onClick={() => triggerDownload(`/filings/${filing.id}/export/checklist.pdf`, `checklist-${filing.id.slice(0,8)}.pdf`)}
                testid="dl-checklist"
              />
            </div>
          </div>

          {/* e-Pay Tax — shown when computed demand > 0 */}
          {(() => {
            const comp = filing.computed_tax?.[filing.regime || "new"] || {};
            const d = filing.data || {};
            const totalTax = comp.total_tax || 0;
            const paid = (d.tds_salary || 0) + (d.tds_other || 0) + (d.advance_tax || 0) + (d.self_assessment_tax || 0);
            const demand = Math.max(0, totalTax - paid);
            if (demand <= 0) return null;
            const ay = filing.assessment_year || "2026-27";
            const ayCode = ay.replace("-", "");
            const pan = d.pan || "";
            const epayUrl = `https://eportal.incometax.gov.in/iec/foservices/#/e-pay-tax?PAN=${pan}&AY=${ayCode}&amt=${Math.round(demand)}`;
            return (
              <div className="mt-6 rounded-lg border border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-rose-700 dark:text-rose-400">
                        Tax demand of {inr(demand)} for AY {ay}
                      </div>
                      <div className="text-xs text-rose-600/80 dark:text-rose-400/70 mt-0.5">
                        TDS + advance tax paid ({inr(paid)}) is less than computed tax ({inr(totalTax)}). Pay the balance before uploading your return to avoid interest u/s 234B.
                      </div>
                    </div>
                  </div>
                  <a href={epayUrl} target="_blank" rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium">
                    Pay at incometax.gov.in <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            );
          })()}

          {validation && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Validation report</div>
              <Card className="mt-3 p-4 border-border">
                <div className="text-sm">
                  {validation.is_valid ? (
                    <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Local checks passed — JSON is ready to upload.</div>
                  ) : (
                    <div className="flex items-center gap-2 text-rose-700"><AlertCircle className="h-4 w-4" /> Issues found — fix these before upload.</div>
                  )}
                </div>
                {validation.issues?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs">
                    {validation.issues.map((i, k) => <li key={k} className="text-rose-700">• <b>{i.code}:</b> {i.msg}</li>)}
                  </ul>
                )}
                {validation.warnings?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs">
                    {validation.warnings.map((w, k) => <li key={k} className="text-amber-700">• <b>{w.code}:</b> {w.msg}</li>)}
                  </ul>
                )}
                <div className="text-[10px] text-muted-foreground mt-3">{validation.note}</div>
              </Card>
            </div>
          )}

          {/* E-verification guide — shown after user selects self-filing */}
          {isAwaitingSubmission && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Step 2 — E-verify at incometax.gov.in</div>
              <p className="mt-2 text-sm text-muted-foreground">After uploading the JSON at the Government portal, complete e-verification using one of these methods. <span className="text-rose-600 font-medium">You have 30 days from the date of filing to e-verify — unverified returns are treated as not filed.</span></p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-4 flex gap-3">
                  <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-primary/8 text-primary"><Smartphone className="h-4 w-4" /></div>
                  <div>
                    <div className="text-sm font-semibold">Aadhaar OTP</div>
                    <div className="text-xs text-muted-foreground mt-0.5">OTP sent to the mobile number linked to your Aadhaar. Instant. Most common method.</div>
                  </div>
                </div>
                <div className="rounded-lg border border-border p-4 flex gap-3">
                  <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-primary/8 text-primary"><Globe className="h-4 w-4" /></div>
                  <div>
                    <div className="text-sm font-semibold">Net Banking EVC</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Log into your bank's net banking and generate an Electronic Verification Code (EVC).</div>
                  </div>
                </div>
                <div className="rounded-lg border border-border p-4 flex gap-3">
                  <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-primary/8 text-primary"><CreditCard className="h-4 w-4" /></div>
                  <div>
                    <div className="text-sm font-semibold">Bank Account / Demat EVC</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Pre-validate your bank or demat account on the portal and generate an EVC from your account details.</div>
                  </div>
                </div>
                <div className="rounded-lg border border-border p-4 flex gap-3">
                  <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-primary/8 text-primary"><Key className="h-4 w-4" /></div>
                  <div>
                    <div className="text-sm font-semibold">Digital Signature (DSC)</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Use a Class-2 or Class-3 DSC. Required for companies and audited taxpayers; optional for individuals.</div>
                  </div>
                </div>
              </div>

              {/* ITR-V acknowledgement recording */}
              <div className="mt-6 rounded-lg border border-border bg-card p-5">
                <div className="text-sm font-semibold">Step 3 — Record your ITR-V acknowledgement</div>
                <p className="mt-1 text-xs text-muted-foreground">After e-verification is complete, the Government portal issues a 15-character ITR-V acknowledgement number. Enter it below to mark your return as officially filed.</p>
                <div className="mt-4 flex gap-3">
                  <Input
                    value={itrVAck}
                    onChange={(e) => setItrVAck(e.target.value.toUpperCase())}
                    placeholder="e.g. 123456789012345"
                    className="h-10 font-mono text-sm"
                    maxLength={20}
                  />
                  <Button onClick={markSelfFiled} disabled={saving || itrVAck.trim().length < 12} className="h-10 shrink-0">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark as filed"}
                  </Button>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">This acknowledgement number is only issued by incometax.gov.in — do not enter any other reference number.</p>
              </div>
            </div>
          )}

          {/* Filed with Government — completion state */}
          {isFiled && (
            <div className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30 p-6">
              <div className="flex items-start gap-4">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300"><PartyPopper className="h-5 w-5" /></div>
                <div>
                  <div className="font-display text-base font-semibold text-emerald-800 dark:text-emerald-200">Return filed with the Income Tax Department</div>
                  {filing.itr_v_acknowledgement && (
                    <div className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                      ITR-V acknowledgement: <span className="font-mono font-semibold">{filing.itr_v_acknowledgement}</span>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">Keep this acknowledgement number safe. You may receive a refund (if applicable) within 20–45 working days, and an intimation under Section 143(1) will be issued at your registered email.</p>
                </div>
              </div>
            </div>
          )}

          {/* Two-option chooser — hide once filed */}
          {!isFiled && <div className="mt-8 grid md:grid-cols-2 gap-4">
            <Card className="p-6 border-border" data-testid="option-a">
              <div className="text-xs uppercase tracking-[0.18em] text-primary font-bold">Option A</div>
              <h3 className="mt-2 font-display text-lg font-semibold">Self-file at incometax.gov.in</h3>
              <p className="mt-2 text-sm text-muted-foreground">You upload the JSON yourself at the Government portal. Free.</p>
              <ol className="mt-4 text-xs space-y-1.5 list-decimal pl-5 text-muted-foreground">
                <li>Log in at <a className="underline" target="_blank" rel="noreferrer" href="https://incometax.gov.in">incometax.gov.in</a> with your PAN</li>
                <li>Go to e-File → Income Tax Return → File Income Tax Return</li>
                <li>Choose Offline / JSON Upload and select the downloaded itr.json</li>
                <li>Validate and e-verify (Aadhaar OTP / Net banking / EVC / DSC)</li>
                <li>Save the official ITR-V acknowledgement issued by the Govt portal</li>
              </ol>
              <Button onClick={confirmSelfSubmission} variant="outline" className="mt-5 w-full" data-testid="confirm-self">
                <ExternalLink className="h-4 w-4 mr-2" /> I'll upload it myself
              </Button>
            </Card>

            <Card className="p-6 border-primary bg-primary/5" data-testid="option-b">
              <div className="text-xs uppercase tracking-[0.18em] text-primary font-bold">Option B · Recommended</div>
              <h3 className="mt-2 font-display text-lg font-semibold">CA-assisted filing</h3>
              <p className="mt-2 text-sm text-muted-foreground">A Chartered Accountant from our team reviews, files at incometax.gov.in, and shares the real ITR-V with you.</p>
              <ul className="mt-4 text-xs space-y-1.5 text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent mt-0.5" /> Document collection assistance</li>
                <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent mt-0.5" /> Secure CA chat & status tracking</li>
                <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent mt-0.5" /> Filed on Government portal by a CA</li>
                <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent mt-0.5" /> Real ITR-V acknowledgement shared on completion</li>
              </ul>
              <Dialog open={caDialogOpen} onOpenChange={setCaDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="mt-5 w-full" data-testid="request-ca-btn"><Users className="h-4 w-4 mr-2" /> Request CA filing</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Request CA-assisted filing</DialogTitle>
                    <DialogDescription>Anything specific our CA team should know about your return?</DialogDescription>
                  </DialogHeader>
                  <Textarea value={caNotes} onChange={(e) => setCaNotes(e.target.value)} placeholder="e.g. I sold ESOPs in Feb 2025; please verify capital gain calculation." rows={4} />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCaDialogOpen(false)}>Cancel</Button>
                    <Button onClick={requestCa} disabled={saving} data-testid="confirm-request-ca">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Card>
          </div>}
        </>
      )}
    </div>
  );
}

function DownloadCard({ icon: Ic, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick} className="text-left rounded-md border border-border hover:border-primary p-4 transition-all hover:-translate-y-0.5 bg-card" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/8 text-primary"><Ic className="h-4 w-4" /></div>
        <div className="flex-1">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
        </div>
        <Download className="h-4 w-4 text-muted-foreground" />
      </div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function NField({ label, k, data, set }) {
  return (
    <Field label={label}>
      <Input
        type="number"
        inputMode="numeric"
        value={data[k] ?? ""}
        onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))}
        className="h-11 num-rupee"
        data-testid={`f-${k}`}
        placeholder="0"
      />
    </Field>
  );
}
